import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ButtonBase, Typography } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import {
    SortableContainer,
    SortableElement,
    arrayMove,
} from "react-sortable-hoc";
import { AppContext } from "../../../AppContext";
import { useItemGroups } from "../../../shared/hooks/useItemsData";
import { useStyles } from "../../style";
import GroupItem from "./GroupItem";
import _ from "lodash";
import { useTranslation } from "react-i18next";

// SortableItem component for groups (vertical layout)
const SortableItemVertical = SortableElement(
    ({ group, active, onClick, isDraggable, t }) => (
        <div
            style={{
                marginBottom: 8,
                cursor: isDraggable ? "grab" : "pointer",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <div style={{ position: "relative" }}>
                <GroupItem active={active} group={group} onClick={onClick} />
                {isDraggable && (
                    <div
                        style={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            background: "rgba(0,0,0,0.6)",
                            borderRadius: "50%",
                            width: 16,
                            height: 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <DragIndicatorIcon
                            style={{ fontSize: 16, color: "white" }}
                        />
                    </div>
                )}
            </div>
            <span
                style={{
                    marginTop: 4,
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                }}
            >
                {group.name === "All Item Groups"
                    ? t?.("items.allItemGroups") || group.name
                    : group.name}
            </span>
        </div>
    )
);

// SortableItem component for groups (horizontal layout for mobile)
const SortableItemHorizontal = SortableElement(
    ({ group, active, onClick, isDraggable, t }) => (
        <div
            style={{
                minWidth: 70,
                marginRight: 8,
                marginBottom: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: isDraggable ? "grab" : "pointer",
                position: "relative",
            }}
        >
            <div style={{ position: "relative" }}>
                <GroupItem active={active} group={group} onClick={onClick} />
                {isDraggable && (
                    <div
                        style={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            background: "rgba(0,0,0,0.6)",
                            borderRadius: "50%",
                            width: 16,
                            height: 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <DragIndicatorIcon
                            style={{ fontSize: 16, color: "white" }}
                        />
                    </div>
                )}
            </div>
            <span
                style={{
                    fontSize: 16,
                    textAlign: "center",
                    marginTop: 4,
                    fontWeight: "700",
                    textTransform: "uppercase",
                }}
            >
                {group.name === "All Item Groups"
                    ? t?.("items.allItemGroups") || group.name
                    : group.name}
            </span>
        </div>
    )
);

// SortableList component for groups (vertical layout)
const SortableListVertical = SortableContainer(
    ({ groups, selectedGroup, onClick, isDraggable, t }) => (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
                overflowY: "auto",
            }}
        >
            {groups.map((group, index) => (
                <SortableItemVertical
                    key={`group-${group.id}`}
                    index={index}
                    group={group}
                    active={selectedGroup === group.id}
                    onClick={onClick}
                    isDraggable={isDraggable}
                    t={t}
                />
            ))}
        </div>
    )
);

// SortableList component for groups (horizontal layout for mobile)
const SortableListHorizontal = SortableContainer(
    ({ groups, selectedGroup, onClick, isDraggable, t }) => (
        <div
            style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "auto",
                overflowX: "auto",
                overflowY: "hidden",
                gap: "8px",
            }}
        >
            {groups.map((group, index) => (
                <SortableItemHorizontal
                    key={`group-${group.id}`}
                    index={index}
                    group={group}
                    active={selectedGroup === group.id}
                    onClick={onClick}
                    isDraggable={isDraggable}
                    t={t}
                />
            ))}
        </div>
    )
);

export default function GroupList({
    onClick,
    isDragDropMode = false,
    horizontal = false,
    ...other
}) {
    const { t } = useTranslation();
    const { menuAllowedGroups, menuRootGroupId } = useContext(AppContext);
    const { isLoading, data, isFetching } = useItemGroups({
        allowedGroups: menuAllowedGroups,
    });

    const rootGroup = { id: menuRootGroupId, name: menuRootGroupId };

    const groupData = useMemo(() => {
        if(data) {
            if (menuAllowedGroups && menuAllowedGroups.length > 0) {
                // Merge groups under all allowed roots (when multiple are allowed)
                return _.flatten(
                    menuAllowedGroups.map((g) => data[g] || [])
                );
            }
            return data["All Item Groups"];
    
        }
    }, [data, menuAllowedGroups]);

    const classes = useStyles();
    const { userMenuSettings, updateUserMenuSettings } = useContext(AppContext);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [sortedGroups, setSortedGroups] = useState([]);

    const handleClick = (group) => {
        setSelectedGroup(group.id);
        onClick(group);
    };

    useEffect(() => {
        if (data && groupData) {
            console.log("data", data);
            let groups = [...groupData];

            // Sort groups based on saved order
            if (
                userMenuSettings &&
                userMenuSettings.item_group_index &&
                userMenuSettings.item_group_index.length > 0
            ) {
                const groupIndexMap = userMenuSettings.item_group_index.reduce(
                    (acc, item) => {
                        acc[item.group_id] = item.index;
                        return acc;
                    },
                    {}
                );

                groups = groups.sort((a, b) => {
                    const indexA = groupIndexMap[a.id] || 0;
                    const indexB = groupIndexMap[b.id] || 0;
                    return indexA - indexB;
                });
            }

            setSortedGroups(groups);
        }
    }, [data, userMenuSettings]);

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const updatedGroups = arrayMove(sortedGroups, oldIndex, newIndex);
        setSortedGroups(updatedGroups);
        handleSaveGroupOrder(updatedGroups);
    };

    const handleSaveGroupOrder = (reorderedGroups) => {
        updateUserMenuSettings({
            item_group_index: reorderedGroups.map((group, index) => ({
                group_id: group.id,
                index,
            })),
        });
    };

    return (
        <>
            {isLoading ? (
                <Typography variant="body2">{t("items.loading")}</Typography>
            ) : groupData ? (
                <>
                    <ButtonBase
                        style={{
                            height: "32px",
                            width: horizontal ? "auto" : "100%",
                            flex: 1,
                            marginBottom: horizontal ? 0 : "1rem",
                            marginRight: horizontal ? "1rem" : 0,
                        }}
                    >
                        <Typography
                            variant="h8"
                            onClick={() => handleClick(rootGroup)}
                        >
                            {rootGroup.name === "All Item Groups"
                                ? t("items.allItemGroups")
                                : rootGroup.name}
                        </Typography>
                    </ButtonBase>
                    {isDragDropMode ? (
                        // Use appropriate sortable list based on layout (horizontal for mobile, vertical for desktop)
                        horizontal ? (
                            <SortableListHorizontal
                                groups={sortedGroups}
                                onSortEnd={onSortEnd}
                                selectedGroup={selectedGroup}
                                onClick={handleClick}
                                axis="x"
                                isDraggable={isDragDropMode}
                                t={t}
                            />
                        ) : (
                            <SortableListVertical
                                groups={sortedGroups}
                                onSortEnd={onSortEnd}
                                selectedGroup={selectedGroup}
                                onClick={handleClick}
                                axis="y"
                                isDraggable={isDragDropMode}
                                t={t}
                            />
                        )
                    ) : // Non-draggable layout
                    horizontal ? (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                height: "auto",
                                overflowX: "auto",
                                overflowY: "hidden",
                                gap: "8px",
                            }}
                        >
                            {sortedGroups.map((item) => (
                                <div
                                    key={`Category_item_${item.name}`}
                                    style={{
                                        minWidth: 70,
                                        marginRight: 8,
                                        marginBottom: 8,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                    }}
                                >
                                    <GroupItem
                                        active={selectedGroup === item.id}
                                        group={item}
                                        onClick={handleClick}
                                    />
                                    <span
                                        style={{
                                            fontSize: 16,
                                            textAlign: "center",
                                            marginTop: 4,
                                            fontWeight: "700",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {item.name === "All Item Groups"
                                            ? t("items.allItemGroups")
                                            : item.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                                overflowY: "auto",
                            }}
                        >
                            {sortedGroups.map((item) => (
                                <div
                                    key={`Category_item_${item.name}`}
                                    className={classes.PageWrap}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        marginBottom: 8,
                                    }}
                                >
                                    <GroupItem
                                        active={selectedGroup === item.id}
                                        group={item}
                                        onClick={handleClick}
                                    />
                                    <span
                                        style={{
                                            marginTop: 4,
                                            textAlign: "center",
                                            fontSize: "16px",
                                            fontWeight: "700",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {item.name === "All Item Groups"
                                            ? t("items.allItemGroups")
                                            : item.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <Typography variant="body2">{t("items.noGroups")}</Typography>
            )}
        </>
    );
}
